import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { HOME_LOANS_VISIBLE } from '../../lib/homeLoans';
import { Menu, X } from 'lucide-react';

/**
 * InstitutionalNavbar — Phase 98 light direction.
 *
 * Always renders as a light, hairline-bordered navbar (no transparent mode)
 * so it sits well on the new paper-led hero. Uses DS tokens / utility classes
 * so visual rules stay in sync with the rest of the public site.
 */
export const InstitutionalNavbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!isMobileMenuOpen) return undefined;
    const closeOnEscape = event => { if (event.key === 'Escape') setIsMobileMenuOpen(false); };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [isMobileMenuOpen]);

  const navLinks = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    ...(HOME_LOANS_VISIBLE ? [{ name: 'Home Loans', href: '/home-loans' }] : []),
    { name: 'Capital Solutions', href: '/capital-solutions' },
    { name: 'For Borrowers', href: '/for-borrowers' },
    { name: 'For Capital Partners', href: '/for-lenders' },
    { name: 'Insights', href: '/insights' },
    { name: 'Tools', href: '/tools' },
    { name: 'Contact', href: '/contact' },
  ];

  return (
    <><a className="gpc-skip-link" href="#public-content" onClick={() => { const target = document.querySelector("main, article, nav + div, nav + section"); if(target){target.id="public-content";target.tabIndex=-1;target.focus();} }}>Skip to content</a><nav
      className="fixed top-0 left-0 right-0 z-50"
      style={{
        background: isScrolled ? 'rgba(246,244,239,0.92)' : 'var(--gpc-paper)',
        backdropFilter: isScrolled ? 'saturate(180%) blur(10px)' : 'none',
        WebkitBackdropFilter: isScrolled ? 'saturate(180%) blur(10px)' : 'none',
        borderBottom: '1px solid var(--gpc-line-soft)',
        transition: 'background-color 0.25s ease, backdrop-filter 0.25s ease',
      }}
    >
      <div className="max-w-screen-2xl mx-auto px-4 lg:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo — brand asset. Colours are intentionally HARDCODED so no
              palette pass, CSS variable, currentColor or theme can repaint
              it. Frame + G = navy #0f2847, P = teal #2d8a8a (original brand). */}
          <Link to="/" className="flex items-center gap-3" data-testid="navbar-logo">
            <svg width="38" height="38" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" data-brand-asset="gp-cap-logo">
              <rect x="4" y="4" width="72" height="72" rx="3" stroke="#0f2847" strokeWidth="2.5" fill="none" />
              <text x="40" y="38" textAnchor="middle" fontFamily="Space Grotesk, Arial, sans-serif" fontSize="28" fontWeight="600" fill="#0f2847">G</text>
              <text x="40" y="62" textAnchor="middle" fontFamily="Space Grotesk, Arial, sans-serif" fontSize="28" fontWeight="600" fill="#2d8a8a">P</text>
              <line x1="20" y1="44" x2="60" y2="44" stroke="#0f2847" strokeWidth="0.5" opacity="0.3" />
            </svg>
            <div className="flex flex-col">
              <span style={{ fontFamily: 'Space Grotesk, system-ui, sans-serif', fontWeight: 500, fontSize: '1.05rem', lineHeight: 1, color: 'var(--gpc-ink)' }}>
                GP Capital
              </span>
              <span style={{ fontFamily: 'Space Grotesk, system-ui, sans-serif', fontSize: '0.6rem', letterSpacing: '0.16em', color: 'var(--gpc-ink-soft)', marginTop: 2 }}>
                HOME LOANS &amp; PRIVATE CAPITAL
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden xl:flex items-center gap-3">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                style={{
                  fontFamily: 'Inter, system-ui, sans-serif',
                  fontSize: '0.8rem',
                  fontWeight: 400,
                  color: 'var(--gpc-ink-soft)',
                  transition: 'color 0.2s ease',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--gpc-ink)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--gpc-ink-soft)'; }}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Desktop CTAs */}
          <div className="hidden xl:flex items-center gap-3">
            <Link
              to="/portal/login"
              style={{
                fontFamily: 'Inter, system-ui, sans-serif',
                fontSize: '0.8rem',
                fontWeight: 500,
                color: 'var(--gpc-ink)',
                padding: '8px 14px',
                transition: 'opacity 0.2s ease',
              }}
              data-testid="navbar-enter-platform"
            >
              Platform sign in
            </Link>

          </div>

          {/* Mobile menu button */}
          <button
            className="xl:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            style={{ color: 'var(--gpc-ink)' }}
            aria-label="Toggle menu" aria-expanded={isMobileMenuOpen} aria-controls="public-mobile-menu"
            data-testid="navbar-mobile-toggle"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div
            className="xl:hidden pb-5"
            style={{ background: 'var(--gpc-paper)', borderTop: '1px solid var(--gpc-line-soft)', maxHeight: 'calc(100dvh - 4rem)', overflowY: 'auto' }}
            id="public-mobile-menu" data-testid="navbar-mobile-menu"
          >
            <div className="flex flex-col gap-1 px-2 pt-3">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className="py-3 px-2"
                  style={{
                    fontFamily: 'Inter, system-ui, sans-serif',
                    fontSize: '1rem',
                    color: 'var(--gpc-ink)',
                    borderBottom: '1px solid var(--gpc-line-soft)',
                  }}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.name}
                </Link>
              ))}
              <div className="flex flex-col gap-2 pt-4 px-2">
                <Link to="/portal/login" className="gpc-cta-secondary" onClick={() => setIsMobileMenuOpen(false)}>
                  Platform sign in
                </Link>
                <Link to="/contact" className="gpc-cta-primary" onClick={() => setIsMobileMenuOpen(false)}>
                  Submit an enquiry
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav></>
  );
};

export default InstitutionalNavbar;
